import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { TrendingUp, TrendingDown, Wallet, Receipt, Info, Download, Loader2 } from "lucide-react";
import api, { fmtIDR } from "@/lib_app/api";
import { toast } from "sonner";

const fmtCompactIDR = (v) => {
  const n = Number(v || 0);
  if (n >= 1e12) return `${(n / 1e12).toFixed(1)} T`;
  if (n >= 1e9) return `${(n / 1e9).toFixed(1)} M`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(0)} Jt`;
  return n.toLocaleString("id-ID");
};

export default function ProfitLossReport() {
  const [period, setPeriod] = useState("ytd");
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ summary: { revenue: 0, cogs: 0, gross: 0, gross_pct: 0, project_count: 0 }, monthly: [], projects: [] });

  useEffect(() => {
    let active = true;
    setLoading(true);
    api.get("/analytics/profit-loss", { params: { period } })
      .then((r) => { if (active) setData(r.data); })
      .catch(() => { if (active) toast.error("Gagal memuat Laporan Laba Rugi"); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [period]);

  const { summary, monthly, projects } = data;

  return (
    <div className="space-y-6" data-testid="pl-report-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div className="flex-1">
          <p className="text-xs uppercase tracking-[0.2em] text-[#A0A3A8] mb-2 font-semibold">Laporan Perusahaan</p>
          <h1 className="text-3xl md:text-[34px] tracking-tight font-bold text-[#090808]">Laporan Laba Rugi</h1>
          <p className="text-sm text-[#A0A3A8] mt-1">Pendapatan dikurangi beban pokok penjualan (biaya vendor) untuk periode terpilih.</p>
        </div>
        <div>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger data-testid="pl-period-filter" className="h-11 rounded-full bg-white card-soft border-transparent px-5 w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="month">Bulan ini</SelectItem>
              <SelectItem value="ytd">YTD (tahun ini)</SelectItem>
              <SelectItem value="year">Tahun Fiskal</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24 text-[#A0A3A8]" data-testid="pl-loading">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Memuat laporan...
        </div>
      ) : (
        <>
          {/* KPI cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard label="Total Pendapatan" value={fmtIDR(summary.revenue)} icon={<Receipt className="w-4 h-4" />} accent="blue" testid="kpi-pl-revenue" />
            <KpiCard label="Total HPP (Beban Vendor)" value={fmtIDR(summary.cogs)} icon={<Wallet className="w-4 h-4" />} accent="rose" testid="kpi-pl-cogs" />
            <KpiCard
              label="Laba Kotor"
              value={fmtIDR(summary.gross)}
              icon={summary.gross >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
              accent={summary.gross >= 0 ? "emerald" : "rose"}
              sub={<span className="text-[10px] text-[#A0A3A8]">{summary.gross_pct.toFixed(2)}% margin kotor</span>}
              testid="kpi-pl-gross"
            />
            <KpiCard label="Proyek Dihitung" value={String(summary.project_count)} icon={<Info className="w-4 h-4" />} accent="slate" sub={<span className="text-[10px] text-[#A0A3A8]">periode terpilih</span>} testid="kpi-pl-count" />
          </div>

          {/* Monthly trend */}
          <Card className="p-5 border-0 rounded-2xl card-soft">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-[#090808]">Tren Pendapatan vs HPP — 6 Bulan Terakhir</h3>
              <span className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-semibold">Pendapatan • HPP • Laba Kotor</span>
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <ComposedChart data={monthly.map((m) => ({ ...m, gross: m.revenue - m.cogs }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#DCE0E7" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#A0A3A8" }} />
                <YAxis tickFormatter={fmtCompactIDR} tick={{ fontSize: 10, fill: "#A0A3A8" }} width={68} />
                <Tooltip formatter={(v, n) => [fmtIDR(v), n]} contentStyle={{ borderRadius: 12, border: "1px solid #DCE0E7", fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="revenue" name="Pendapatan" fill="#97ADD4" radius={[8, 8, 0, 0]} />
                <Bar dataKey="cogs" name="HPP" fill="#FF8C9B" radius={[8, 8, 0, 0]} />
                <Line type="monotone" dataKey="gross" name="Laba Kotor" stroke="#3EB876" strokeWidth={2.5} dot={{ r: 3 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </Card>

          {/* Formal income-statement table */}
          <Card className="border-0 rounded-2xl card-soft overflow-hidden" data-testid="pl-statement-table">
            <div className="px-5 py-3 border-b border-[#DCE0E7]">
              <h3 className="text-sm font-bold text-[#090808]">Laporan Laba Rugi — Format Ringkas</h3>
            </div>
            <div className="p-5">
              <table className="w-full text-sm">
                <tbody>
                  <Row label="PENDAPATAN" bold section />
                  <Row label="Pendapatan Jasa / Proyek (nilai kontrak, sebelum PPN)" value={summary.revenue} indent />
                  <Row label="Total Pendapatan" value={summary.revenue} bold topBorder />

                  <Row label="BEBAN POKOK PENJUALAN (HPP)" bold section spacer />
                  <Row label="Beban Vendor (Purchase Order, sebelum PPN)" value={-summary.cogs} indent negative />
                  <Row label="Total Beban Pokok Penjualan" value={-summary.cogs} bold topBorder negative />

                  <Row label="LABA KOTOR" value={summary.gross} bold big highlight={summary.gross >= 0 ? "green" : "red"} pct={summary.gross_pct} spacer />

                  <Row label="BEBAN OPERASIONAL (gaji, sewa, dll)" bold section spacer />
                  <Row label="Belum tercatat di sistem" value={null} indent muted />

                  <Row label="LABA BERSIH (estimasi, = Laba Kotor)" value={summary.gross} bold big highlight={summary.gross >= 0 ? "green" : "red"} pct={summary.gross_pct} topBorder spacer />
                </tbody>
              </table>
              <div className="mt-4 flex items-start gap-2 text-xs text-[#A0A3A8] bg-[#F4F5F7] rounded-xl p-3">
                <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>Beban operasional (gaji tim, sewa kantor, utilitas, dll) belum ada modul pencatatannya di sistem, sehingga Laba Bersih saat ini disamakan dengan Laba Kotor. Bila diperlukan, modul input biaya operasional bisa ditambahkan agar Laba Bersih lebih akurat.</span>
              </div>
            </div>
          </Card>

          {/* Breakdown per project */}
          <Card className="border-0 rounded-2xl card-soft overflow-hidden" data-testid="pl-project-breakdown">
            <div className="px-5 py-3 border-b border-[#DCE0E7] flex items-center justify-between">
              <h3 className="text-sm font-bold text-[#090808]">Margin per Proyek</h3>
              <span className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-semibold">diurutkan margin terbesar</span>
            </div>
            {projects.length === 0 ? (
              <div className="p-8 text-center text-sm text-[#A0A3A8]" data-testid="pl-project-empty">Belum ada proyek pada periode ini.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#F4F5F7] text-[10px] uppercase tracking-widest text-[#A0A3A8] font-semibold">
                    <tr>
                      <th className="text-left px-5 py-3">Proyek</th>
                      <th className="text-left px-3 py-3">Klien</th>
                      <th className="text-right px-3 py-3">Pendapatan</th>
                      <th className="text-right px-3 py-3">Beban Vendor</th>
                      <th className="text-right px-5 py-3">Margin</th>
                    </tr>
                  </thead>
                  <tbody>
                    {projects.map((p) => (
                      <tr key={p.id} className="border-t border-[#DCE0E7]" data-testid={`pl-project-row-${p.id}`}>
                        <td className="px-5 py-3 font-semibold text-[#090808] truncate max-w-[260px]">{p.name}</td>
                        <td className="px-3 py-3 text-[#626061] truncate max-w-[180px]">{p.client_name}</td>
                        <td className="px-3 py-3 text-right font-num tabular-nums text-[#3B4A63]">{fmtIDR(p.revenue)}</td>
                        <td className="px-3 py-3 text-right font-num tabular-nums text-[#CE6968]">{fmtIDR(p.cost)}</td>
                        <td className="px-5 py-3 text-right">
                          <div className={`font-bold font-num tabular-nums ${p.margin >= 0 ? "text-[#3EB876]" : "text-[#CE6968]"}`}>{fmtIDR(p.margin)}</div>
                          <div className={`text-[10px] font-num tabular-nums ${p.margin >= 0 ? "text-[#3EB876]" : "text-[#CE6968]"}`}>{p.margin_pct.toFixed(2)}%</div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <div className="flex justify-end pt-2">
            <Button disabled data-testid="pl-export-btn-disabled" className="gap-2 h-11 rounded-full bg-[#DCE0E7] text-[#A0A3A8] px-6 font-semibold cursor-not-allowed">
              <Download className="w-4 h-4" /> Export PDF (segera hadir)
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

function KpiCard({ label, value, icon, accent = "slate", sub, testid }) {
  const tint = {
    blue: "bg-[#DCE0E7] text-[#3B4A63]",
    emerald: "bg-[#C9F0D6] text-[#3EB876]",
    rose: "bg-[#FFD7B5] text-[#CE6968]",
    slate: "bg-[#DCE0E7] text-[#626061]",
  }[accent];
  return (
    <Card className="p-4 border-0 rounded-2xl card-soft" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-semibold">{label}</span>
        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${tint}`}>{icon}</div>
      </div>
      <div className="text-xl font-bold text-[#090808]">{value}</div>
      {sub && <div className="mt-1">{sub}</div>}
    </Card>
  );
}

function Row({ label, value, bold, section, indent, negative, highlight, pct, topBorder, spacer, big, muted }) {
  const color = muted
    ? "text-[#A0A3A8] italic"
    : highlight === "green" ? "text-[#3EB876]" : highlight === "red" ? "text-[#CE6968]" : negative ? "text-[#CE6968]" : "text-[#090808]";
  return (
    <tr className={`${topBorder ? "border-t border-[#DCE0E7]" : ""} ${spacer ? "[&>td]:pt-4" : ""}`}>
      <td className={`py-1.5 ${indent ? "pl-5 text-[#626061]" : ""} ${section ? "text-[11px] uppercase tracking-widest text-[#A0A3A8] font-bold" : ""} ${bold && !section ? "font-bold text-[#090808]" : ""} ${big ? "text-base" : "text-sm"}`}>
        {label}
      </td>
      <td className={`py-1.5 text-right font-num tabular-nums ${bold ? "font-bold" : ""} ${big ? "text-base" : "text-sm"} ${color}`}>
        {value === undefined ? "" : value === null ? "—" : fmtIDR(value)}
        {pct !== undefined && <span className="text-[11px] font-semibold ml-2">({pct.toFixed(2)}%)</span>}
      </td>
    </tr>
  );
}
